// imports unchanged
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { yupResolver } from '@hookform/resolvers/yup'

import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'
import FileInputUploadFieldNew from 'components/styledFormElements/FineInputUploadNew'
import VideoDropzone from 'components/styledFormElements/VideoDropzone'
import LabelsList from 'components/label/LabelList'
import {
    useGetEpisodeDetails,
    useUpdateEpisode,
    presignEpisodeFile,
    uploadFileToPresignedUrl,
    updateEpisodeUpload,
    PresignFileType,
} from 'hooks/episode'
import { QueryKeys } from 'hooks/_query_keys'
import CircularProgress from 'components/styledFormElements/CircularProgress'
import Tooltip from 'components/styledFormElements/Tooltip'
import { FileSizeLimit } from 'constants/file-validation'
import InputField from 'components/styledFormElements/InputField'
import { editEpisodeSchema } from './validation'
import { parseLabels } from '../Collections/helper'
import { ConfirmModal } from 'components/confirmationPopup'
import PdfUploadField from 'components/styledFormElements/PdfUploadField'
import { TranscriptUploadField } from 'components/styledFormElements/TranscriptUploadField'
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { useGenerateLabelsFromFile, useGenerateLabelsFromText } from 'hooks/ai-label-generation'
import { SparklesIcon } from '@heroicons/react/24/outline'

interface VideoFileData {
    file: File | null
    duration: any
    size: any
}

/* ---------------- CONFIRM MODAL ---------------- */
const EditEpisode: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const notification = useNotificationBar()
    const queryClient = useQueryClient()

    const { data, isLoading } = useGetEpisodeDetails(id)
    const { mutate: updateEpisode, isLoading: isSaving } = useUpdateEpisode()
    const { data: activePrompt } = useActiveAIPrompt()
    const generateLabelsFromFile = useGenerateLabelsFromFile()
    const generateLabelsFromText = useGenerateLabelsFromText()

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        control,
        setError,
        clearErrors,
        getValues,
        formState: { errors },
    } = useForm<any>({
        defaultValues: {
            name: '',
            short_summary: '',
            creator: '',
            video_id: '',
            labels: [],
            resourceFile: null,
            thumbnail: null,
            videoFile: null,
            resourceTimestamp: '',
            duration: null,
        },
        resolver: yupResolver(editEpisodeSchema),
    })

    const [labelsArray, setLabelsArray] = useState<string[]>([])
    const [labelInput, setLabelInput] = useState('')
    const [isReplacingVideo, setIsReplacingVideo] = useState(false)
    const [showReplaceModal, setShowReplaceModal] = useState(false)
    const [resourceError, setResourceError] = useState<string | null>(null)
    const [videoFile, setVideoFile] = useState<VideoFileData>({
        file: null,
        duration: null,
        size: null,
    })
    const [transcriptFile, setTranscriptFile] = useState<File | string | null>(null)
    const [isGeneratingLabels, setIsGeneratingLabels] = useState(false)

    // Function to download transcript file from URL and convert to File object
    const downloadTranscriptFile = async (url: string) => {
        try {
            // Use proxy endpoint to bypass CORS
            const proxyUrl = `${process.env.REACT_APP_API_BASE_URL}/transcript/proxy?url=${encodeURIComponent(url)}`
            const response = await fetch(proxyUrl)
            if (!response.ok) {
                console.warn('Failed to fetch transcript file via proxy:', url)
                return
            }
            
            const blob = await response.blob()
            const fileName = url.split('/').pop() || 'transcript.pdf'
            const file = new File([blob], fileName, { type: blob.type })
            
            setTranscriptFile(file)
            setValue('transcriptFile', file, { shouldValidate: true })
            console.log('Successfully downloaded and set transcript file:', file.name)
        } catch (error) {
            console.warn('Error downloading transcript file:', error)
        }
    }

    const watchedThumbnail = useWatch({ control, name: 'thumbnail' })
    const watchedVideoFile = useWatch({ control, name: 'videoFile' })
    const watchedDuration = useWatch({ control, name: 'duration' })
    const watchedResourceTimestamp = useWatch({
        control,
        name: 'resourceTimestamp',
    })
    const resourceFile = useWatch({ control, name: 'resourceFile' })
    const watchedName = useWatch({ control, name: 'name' })
    const watchedShortSummary = useWatch({ control, name: 'short_summary' })
    const showVideoReplaceError = isReplacingVideo && !watchedVideoFile

    const progressRef = useRef<NodeJS.Timeout | null>(null)
    const [uploadProgress, setUploadProgress] = useState(0)
    const submitLockRef = useRef(false)
    const videoDropzoneRef = useRef<HTMLDivElement | null>(null)
    const thumbUrlRef = useRef<string | null>(null)
    const resourceUrlRef = useRef<string | null>(null)
    const previousEpisodeNameRef = useRef<string>('')
    const [open, setOpen] = useState(false)
    const getUploadPhase = (progress: number) => {
        if (progress === 0) return 'idle'
        if (progress <= 20) return 'uploading'
        return 'processing'
    }

    const getUploadLabel = (progress: number) => {
        if (progress <= 20) return 'Uploading video…'
        if (progress <= 40) return 'Transcoding video…'
        return 'Processing video…'
    }

    const uploadPhase = getUploadPhase(uploadProgress)

    useEffect(() => {
        if (uploadProgress === 20 && !progressRef.current) {
            progressRef.current = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 95) {
                        // Clean up interval when we reach the max
                        if (progressRef.current) {
                            clearInterval(progressRef.current)
                            progressRef.current = null
                        }
                        return prev
                    }
                    return prev + 1
                })
            }, 300)
        }

        return () => {
            // Cleanup interval if component updates and uploadProgress changes
            if (progressRef.current) {
                clearInterval(progressRef.current)
                progressRef.current = null
            }
        }
    }, [uploadProgress])

    // Cleanup function for intervals and object URLs
    useEffect(() => {
        return () => {
            // Clear any active intervals
            if (progressRef.current) {
                clearInterval(progressRef.current)
                progressRef.current = null
            }

            // Cleanup object URLs
            if (thumbUrlRef.current) {
                URL.revokeObjectURL(thumbUrlRef.current)
                thumbUrlRef.current = null
            }

            if (resourceUrlRef.current) {
                URL.revokeObjectURL(resourceUrlRef.current)
                resourceUrlRef.current = null
            }
        }
    }, [])

    const episode = data?.data?.results
    const labels = parseLabels(episode?.labels)
    const hasExistingVideo = Boolean(episode?.video_id)

    const [existingResourceUrl, setExistingResourceUrl] = useState<
        string | null
    >(null)

    useEffect(() => {
        if (!episode) return

        setExistingResourceUrl(episode.resource_url ?? null)

        reset({
            name: episode.name,
            creator: episode.creator,
            short_summary: episode.short_summary,
            video_id: episode.video_id,
            duration: episode.duration,
            thumbnail: episode.thumbnail,
            labels: labels,
            resourceFile: episode.resourceFile ?? null,
            resourceTimestamp: episode.resourceTimestamp ?? '',
            transcriptFile: episode.transcriptFile ?? null,
        })
    }, [episode])

    /* ---------- PREFILL ---------- */
    useEffect(() => {
        if (!episode) return

        const labels =
            typeof episode.labels === 'string'
                ? JSON.parse(episode.labels)
                : episode.labels ?? []

        reset({
            name: episode.name,
            creator: episode.creator,
            short_summary: episode.short_summary,
            video_id: episode.video_id,
            duration: episode.duration,
            labels: labels,
            thumbnail: episode.thumbnail,
            resourceFile: episode.resourceFile ?? null,
            resourceTimestamp: episode.resourceTimestamp ?? '',
            transcriptFile: episode.transcriptFile ?? null,
        })

        setLabelsArray(labels)

        // Set transcript file in local state if it exists
        if (episode.transcriptFile) {
            setTranscriptFile(episode.transcriptFile)
            // Auto-download and set as File object if it's a URL
            if (typeof episode.transcriptFile === 'string') {
                downloadTranscriptFile(episode.transcriptFile)
            }
        }
        
        // Initialize previousEpisodeNameRef to prevent duplicate label on edit
        previousEpisodeNameRef.current = episode.name ? normalizeLabel(episode.name) : ''
    }, [episode, reset])

    /* ---------- PREVIEWS ---------- */
    const thumbPreview = useMemo(() => {
        if (!watchedThumbnail) return null
        if (typeof watchedThumbnail === 'string') return watchedThumbnail
        if (watchedThumbnail instanceof File)
            return URL.createObjectURL(watchedThumbnail)
        return null
    }, [watchedThumbnail])

    const resourcePreview = useMemo(() => {
        if (!resourceFile) return existingResourceUrl
        if (typeof resourceFile === 'string') {
            return resourceFile
        }
        if (resourceFile instanceof File) {
            return URL.createObjectURL(resourceFile)
        }
        return existingResourceUrl
    }, [resourceFile, existingResourceUrl])

    /* ---------- VALIDATION ---------- */
    useEffect(() => {
        if (!resourceFile) {
            setResourceError(null)
            return
        }

        // ⛔ DO NOT validate empty timestamp immediately
        if (!watchedResourceTimestamp) {
            setResourceError(null)
            return
        }

        const effectiveDuration =
            isReplacingVideo && watchedVideoFile
                ? watchedDuration
                : episode?.duration

        if (!effectiveDuration) {
            setResourceError('Select a video first')
            return
        }

        if (+watchedResourceTimestamp >= +effectiveDuration) {
            setResourceError(
                `Timestamp must be less than ${Math.floor(effectiveDuration)}s`
            )
            return
        }

        setResourceError(null)
    }, [
        resourceFile,
        watchedResourceTimestamp,
        watchedDuration,
        watchedVideoFile,
        isReplacingVideo,
        episode?.duration,
    ])

    /* ---------- REPLACE VIDEO ---------- */
    const confirmReplaceVideo = () => {
        setShowReplaceModal(false)
        setIsReplacingVideo(true)
        setValue('video_id', '')
        setValue('videoFile', '')
        setValue('resourceFile', '')
        setValue('resourceTimestamp', '')
        setValue('duration', '')

        // ✅ auto-focus dropzone
        setTimeout(() => {
            videoDropzoneRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        }, 150)
    }

    /* ---------- SUBMIT ---------- */
    const onSubmit = async (values: any) => {
        if (isReplacingVideo && !values.videoFile) {
            submitLockRef.current = false
            notification.push({
                title: 'Validation error',
                type: 'error',
                message:
                    'Please upload a new video to replace the existing one',
            })
            return
        }

        if (submitLockRef.current) return // 🛑 hard stop

        submitLockRef.current = true

        if (resourceError) {
            notification.push({
                title: 'Validation error',
                type: 'error',
                message: resourceError,
            })
            submitLockRef.current = false
            return
        }

        if (!labelsArray.length) {
            notification.push({
                title: 'Validation error',
                type: 'error',
                message: 'At least one label is required',
            })
            submitLockRef.current = false
            return
        }

        const onSuccess = () => {
            if (progressRef.current) {
                clearInterval(progressRef.current)
                progressRef.current = null
            }
            submitLockRef.current = false
            setUploadProgress(100)

            notification.push({
                title: 'Success',
                type: 'success',
                message: 'Episode updated successfully',
            })

            queryClient.invalidateQueries([QueryKeys.EPISODES])
            navigate(-1)
        }

        const onError = (err: any) => {
            if (progressRef.current) {
                clearInterval(progressRef.current)
                progressRef.current = null
            }
            submitLockRef.current = false
            setUploadProgress(0)

            notification.push(
                getErrorResponse(err) || {
                    title: 'Error',
                    type: 'error',
                    message: 'Failed to update episode',
                }
            )
        }

        // Only files the user actually replaced go through the presigned S3
        // flow, one at a time, in this order — video goes last. Anything left
        // as a string url stays untouched server-side.
        const filesToUpload: { type: PresignFileType; file: File }[] = []
        if (values.thumbnail instanceof File)
            filesToUpload.push({ type: 'thumbnail', file: values.thumbnail })
        if (values.transcriptFile instanceof File)
            filesToUpload.push({ type: 'transcript', file: values.transcriptFile })
        if (values.resourceFile instanceof File)
            filesToUpload.push({ type: 'resource', file: values.resourceFile })
        if (values.videoFile instanceof File)
            filesToUpload.push({ type: 'video', file: values.videoFile })

        try {
            const payload: any = {
                name: values.name,
                creator: values.creator,
                short_summary: values.short_summary,
                labels: labelsArray,
                resourceTimestamp: values.resourceTimestamp,
            }

            if (!isReplacingVideo && values.video_id) {
                payload.video_id = values.video_id
            }

            let videoAsset: { entryId: string; key: string } | null = null

            if (filesToUpload.length) {
                const totalBytes = filesToUpload.reduce(
                    (sum, { file }) => sum + file.size,
                    0
                )
                let bytesDoneBeforeCurrent = 0
                const keyByType: Partial<Record<PresignFileType, string>> = {}

                for (const { type, file } of filesToUpload) {
                    const asset = await presignEpisodeFile(file.name, file.type, type)
                    const bytesBefore = bytesDoneBeforeCurrent

                    await uploadFileToPresignedUrl(asset.uploadUrl, file, (e) => {
                        if (!e.total) return
                        const loaded = bytesBefore + e.loaded
                        const realPercent = totalBytes
                            ? (loaded / totalBytes) * 100
                            : 100
                        setUploadProgress(Math.min(20, Math.round(realPercent * 0.2)))
                    })

                    bytesDoneBeforeCurrent += file.size
                    keyByType[type] = asset.key
                    if (type === 'video') videoAsset = asset
                }

                if (keyByType.thumbnail) payload.thumbnailKey = keyByType.thumbnail
                if (keyByType.transcript) payload.transcriptKey = keyByType.transcript
                if (keyByType.resource) payload.resourceKey = keyByType.resource
            } else {
                setUploadProgress(20)
            }

            if (videoAsset) {
                // Video was replaced — update-upload persists the new video +
                // metadata against this existing episode.
                await updateEpisodeUpload({
                    ...payload,
                    episode_id: id,
                    entryId: videoAsset.entryId,
                    key: videoAsset.key,
                    duration: values.duration,
                })
                onSuccess()
            } else {
                updateEpisode(
                    { episode_id: id!, data: payload },
                    { onSuccess, onError }
                )
            }
        } catch (err) {
            onError(err)
        }
    }
    const normalizeLabel = (label: string) => {
        const cleaned = label.trim()
        if (!cleaned) return ''

        return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
    }


    const handleGenerateLabels = async () => {
        const title = getValues('name')
        const description = getValues('short_summary')

        if (!title.trim()) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: 'Episode title is required for AI label generation'
            })
            return
        }

        if (!description || !description.trim()) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: 'Short summary is required for AI label generation'
            })
            return
        }

        if (description.length > 75) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: 'Short Summary cannot exceed 75 characters'
            })
            return
        }

        setIsGeneratingLabels(true)

        try {
            if (!activePrompt) {
                notification.push({
                    title: 'Error',
                    type: 'error',
                    message: 'No active AI prompt found. Please configure one in Settings.'
                })
                return
            }

            let result
            const promptText = activePrompt.prompt

            // Use transcript file if available, otherwise use text-based generation
            console.log('Transcript file state:', transcriptFile, typeof transcriptFile, transcriptFile instanceof File)
            
            if (transcriptFile && transcriptFile instanceof File) {
                console.log('Using transcript file for AI generation')
                result = await generateLabelsFromFile.mutateAsync({
                    file: transcriptFile,
                    title,
                    description,
                    prompt_text: promptText
                })
            } else if (transcriptFile && typeof transcriptFile === 'string') {
                console.log('Downloading transcript from URL for AI generation')
                // Download transcript from URL and use it for generation
                try {
                    // Use proxy endpoint to bypass CORS
                    const proxyUrl = `${process.env.REACT_APP_API_BASE_URL}/transcript/proxy?url=${encodeURIComponent(transcriptFile)}`
                    const response = await fetch(proxyUrl)
                    if (!response.ok) {
                        throw new Error('Failed to fetch transcript file via proxy')
                    }
                    
                    const blob = await response.blob()
                    console.log('Downloaded blob:', {
                        size: blob.size,
                        type: blob.type
                    })
                    
                    const fileName = transcriptFile.split('/').pop() || 'transcript.pdf'
                    const file = new File([blob], fileName, { type: blob.type })
                   
                    console.log('Sending file to API:', {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        title,
                        description
                    })
                    
                    result = await generateLabelsFromFile.mutateAsync({
                        file,
                        title,
                        description,
                        prompt_text: promptText
                    })
                } catch (error) {
                    console.warn('Failed to download transcript (possibly CORS issue), falling back to text generation:', error)
                    notification.push({
                        title: 'Info',
                        type: 'info',
                        message: 'Transcript download failed due to CORS restrictions. Using text-based generation instead.'
                    })
                    // Fallback to text-based generation
                    result = await generateLabelsFromText.mutateAsync({
                        title,
                        description,
                        prompt_text: promptText
                    })
                }
            } else {
                console.log('No transcript file available, using text-based generation')
                // Fallback to text-based generation when no transcript is provided
                result = await generateLabelsFromText.mutateAsync({
                    title,
                    description,
                    prompt_text: promptText
                })
            }

            const labels = result?.data?.results?.labels || result?.results?.labels || result?.labels

            if (labels && Array.isArray(labels)) {
                const normalizedLabels = labels
                    .filter((label: string) => label && label.trim())
                    .map((label: string) => normalizeLabel(label))
                    .filter((label: string, index: number, self: string[]) =>
                        self.indexOf(label) === index
                    )

                // Preserve episode name label
                const episodeName = watchedName?.trim()
                const episodeNameLabel = episodeName ? normalizeLabel(episodeName) : ''

                // Remove episode name from AI labels if it exists (to avoid duplicate) - case insensitive
                const aiLabelsWithoutEpisodeName = normalizedLabels.filter(label => 
                  label.toLowerCase() !== episodeNameLabel.toLowerCase()
                )

                // Combine: episode name first, then AI labels
                const finalLabels = episodeNameLabel
                    ? [episodeNameLabel, ...aiLabelsWithoutEpisodeName]
                    : aiLabelsWithoutEpisodeName

                setLabelsArray(finalLabels)
                setValue('labels', finalLabels, { shouldValidate: true })

                notification.push({
                    title: 'Success',
                    type: 'success',
                    message: result?.data?.message || 'Labels generated successfully'
                })
            }
        } catch (error: any) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: error?.response?.data?.message ?? 'Failed to generate labels. Please try again.'
            })
        } finally {
            setIsGeneratingLabels(false)
        }
    }

    const handleLabelAdd = () => {
        const token = labelInput.trim()
        if (!token) return

        const formatted = normalizeLabel(token)
        const current = getValues('labels')

        if (current.includes(formatted)) return

        const next = [...current, formatted]
        setLabelsArray(next)
        setValue('labels', next, { shouldValidate: true })
        setLabelInput('')
    }

    const hasResource =
        Boolean(existingResourceUrl) || resourceFile instanceof File

    const handleRemoveLabel = useCallback(
        (idx: number) => {
            const labelToRemove = labelsArray[idx]
            const episodeName = watchedName?.trim()

            // Prevent removing the episode name label
            if (episodeName && normalizeLabel(episodeName) === labelToRemove) {
                notification.push({
                    title: 'Info',
                    type: 'info',
                    message: 'Episode name label cannot be removed'
                })
                return
            }

            setLabelsArray((prev) => {
                const next = prev.filter((_, i) => i !== idx)

                // ✅ store real array in RHF
                setValue('labels', next, { shouldValidate: true })

                return next
            })
        },
        [setValue, labelsArray, watchedName, notification]
    )

    // Auto-add episode name as label
    useEffect(() => {
        const episodeName = watchedName?.trim()
        const normalizedName = episodeName ? normalizeLabel(episodeName) : ''
        const previousNormalizedName = previousEpisodeNameRef.current

        // Only update if the normalized name has actually changed
        if (normalizedName !== previousNormalizedName) {
            const currentLabels = getValues('labels') || []

            // Remove the old episode name label if it exists
            const withoutOldName = previousNormalizedName
                ? currentLabels.filter(label => label !== previousNormalizedName)
                : currentLabels

            // Add the new episode name label at the beginning (if not empty and not already present)
            const updatedLabels = normalizedName
                ? (withoutOldName.includes(normalizedName)
                    ? withoutOldName
                    : [normalizedName, ...withoutOldName])
                : withoutOldName

            setLabelsArray(updatedLabels)
            setValue('labels', updatedLabels, { shouldValidate: true })

            // Update the ref to track the current episode name
            previousEpisodeNameRef.current = normalizedName
        }
    }, [watchedName, setValue, getValues])

    const handleCancel = useCallback(() => setOpen(true), [])
    /* ================= UI ================= */
    return (
        <div className="w-full px-6 py-6 bg-gray-50 dark:bg-slate-900">
            {/* ===================== CONFIRM REPLACE MODAL ===================== */}
            {showReplaceModal && (
                <ConfirmModal
                    open
                    message="Are you sure you want to replace the video?"
                    confirmText="Replace"
                    cancelText="Cancel"
                    onCancel={() => setShowReplaceModal(false)}
                    onConfirm={confirmReplaceVideo}
                />
            )}

            {!showReplaceModal && (
                <ConfirmModal
                    open={open}
                    title="Are you sure?"
                    message="You have unsaved changes. Continue without saving?"
                    confirmText="Yes"
                    cancelText="Cancel"
                    variant="danger"
                    onCancel={() => setOpen(false)}
                    onConfirm={() => {
                        setOpen(false)
                        navigate(-1)
                    }}
                />
            )}

            {/* ===================== UPLOAD PROGRESS OVERLAY ===================== */}
            {uploadPhase !== 'idle' && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-8 flex flex-col items-center gap-4 shadow-lg w-72">
                        <CircularProgress percent={uploadProgress} />
                        <p className="text-sm text-center">
                            {getUploadLabel(uploadProgress)} ({uploadProgress}%)
                        </p>
                    </div>
                </div>
            )}

            {/* ===================== MAIN FORM ===================== */}
            {uploadPhase === 'idle' && (
                <>
                    {/* ---------- HEADER ---------- */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                            Edit Episode
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Update episode details, media and metadata
                        </p>
                    </div>

                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="space-y-8"
                    >
                        {/* ===================================================== */}
                        {/* BASIC INFORMATION */}
                        {/* ===================================================== */}
                        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                Basic Information
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputField
                                    label="Episode Name"
                                    required
                                    helpText="Title of the episode displayed to viewers."
                                    placeholder="Enter Episode Name"
                                    type="text"
                                    register={register('name', {
                                        required: 'Episode name is required',
                                    })}
                                    error={errors?.name?.message as string | ''}
                                />

                                <InputField
                                    label="Creator"
                                    required
                                    helpText="Name of the creator or uploader."
                                    placeholder="Enter Creator Name"
                                    type="text"
                                    register={register('creator', {
                                        required: 'Creator is required',
                                    })}
                                    error={
                                        errors?.creator?.message as string | ''
                                    }
                                />
                            </div>

                            <div className="mt-4">
                                <InputField
                                    label="Short Summary"
                                    helpText="Brief description shown in listings and search results."
                                    placeholder="Enter Short Summary"
                                    type="text"
                                    register={register('short_summary')}
                                    error={
                                        errors?.short_summary?.message as
                                        | string
                                        | ''
                                    }
                                />
                            </div>

                            {/* Labels */}
                            <div className="mt-4 flex flex-col space-y-1">
                                <div className="mt-4 flex flex-col space-y-1">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Labels
                                        </label>
                                        <span className="text-red-700">*</span>
                                        <Tooltip
                                            items={[
                                                'Tags to categorize episodes (e.g., Action, Drama)',
                                                'Helps in search and recommendations',
                                            ]}
                                        />
                                    </div>

                                    <div className="flex gap-2">
                                        <input
                                            value={labelInput}
                                            onChange={(e) =>
                                                setLabelInput(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    handleLabelAdd()
                                                }
                                            }}
                                            className="flex-1 rounded px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-sm"
                                            placeholder="Type label and press Enter"
                                            disabled={isGeneratingLabels}
                                        />

                                        <button
                                            type="button"
                                            title={!watchedName?.trim() ? "Episode name is required" : !watchedShortSummary?.trim() ? "Short summary is required" : "Generate labels using AI"}
                                            onClick={handleGenerateLabels}
                                            disabled={isGeneratingLabels || !watchedName?.trim() || !watchedShortSummary?.trim()}
                                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                                        >
                                            <SparklesIcon className="h-4 w-4" />
                                            {isGeneratingLabels ? 'Generating...' : 'AI Generate'}
                                        </button>
                                    </div>

                                    <div className="border border-gray-300 dark:border-gray-600 rounded p-2 max-h-40 overflow-y-auto bg-gray-50 dark:bg-slate-800">
                                        {labelsArray.length ? (
                                            <LabelsList
                                                labels={labelsArray}
                                                onRemove={handleRemoveLabel}
                                            />
                                        ) : (
                                            <span className="text-xs text-gray-400">
                                                No labels added
                                            </span>
                                        )}
                                    </div>
                                    {errors?.labels && (
                                        <span className="text-xs text-red-500">
                                            {
                                                errors.labels.message as
                                                | string
                                                | ''
                                            }
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Transcript Upload */}
                            <div className="mt-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Transcript
                                    </label>
                                    <Tooltip
                                        items={[
                                            'Upload a transcript file (PDF, Word, or TXT)',
                                            'AI will use this to generate more accurate labels',
                                        ]}
                                    />
                                </div>

                                <TranscriptUploadField
                                    value={transcriptFile}
                                    onChange={(file) => {
                                        setTranscriptFile(file)
                                        setValue('transcriptFile', file, { shouldValidate: true })
                                    }}
                                    disabled={isGeneratingLabels}
                                />

                                {transcriptFile && (
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                        ✓ Transcript uploaded. AI will use this for label generation.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ===================================================== */}
                        {/* MEDIA + LINKED RESOURCE */}
                        {/* ===================================================== */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* ---------------- MEDIA ASSETS ---------------- */}
                            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                    Media Assets
                                </h4>

                                <div className="grid grid-cols-1 gap-6">
                                    <FileInputUploadFieldNew
                                        label="Thumbnail"
                                        name="thumbnail"
                                        control={control}
                                        errors={errors}
                                        preview={thumbPreview}
                                        rules={{
                                            required: 'Thumbnail is required',
                                        }}
                                        required
                                        allowedTypes={
                                            FileSizeLimit?.thumbnail
                                                ?.allowedTypes
                                        }
                                        tooltip={
                                            FileSizeLimit?.thumbnail
                                                ?.tooltipItems as any
                                        }
                                        fileSizeLimit={FileSizeLimit?.thumbnail}
                                    />

                                    {/* Existing video info */}

                                    <InputField
                                        label="Video ID"
                                        disabled
                                        register={register('video_id')}
                                    />
                                    {hasExistingVideo && !isReplacingVideo && (
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowReplaceModal(true)
                                                }
                                                className="text-xs text-blue-600 underline self-start"
                                            >
                                                Replace Video
                                            </button>
                                        </div>
                                    )}

                                    <InputField
                                        label="Duration (seconds)"
                                        disabled
                                        register={register('duration')}
                                    />

                                    {(!hasExistingVideo ||
                                        isReplacingVideo) && (
                                            <div ref={videoDropzoneRef}>
                                                <VideoDropzone
                                                    label="Episode Video"
                                                    name="videoFile"
                                                    control={control}
                                                    setError={setError}
                                                    clearErrors={clearErrors}
                                                    rules={{
                                                        required:
                                                            'Video file is required',
                                                    }}
                                                    onVideoMeta={(meta) => {
                                                        setVideoFile(meta)
                                                        setValue(
                                                            'duration',
                                                            Math.floor(
                                                                meta?.duration || 0
                                                            ),
                                                            {
                                                                shouldValidate:
                                                                    true,
                                                            }
                                                        )
                                                    }}
                                                    required
                                                    tooltip={
                                                        FileSizeLimit?.video
                                                            ?.tooltipItems as any
                                                    }
                                                    fileSizeLimit={{
                                                        max_size:
                                                            FileSizeLimit.video
                                                                .max_size,
                                                        message:
                                                            FileSizeLimit.video
                                                                .message,
                                                    }}
                                                />
                                                {showVideoReplaceError && (
                                                    <p className="mt-2 text-sm text-red-600">
                                                        Please upload a new video to
                                                        replace the existing one
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* ---------------- LINKED RESOURCE ---------------- */}
                            <div className="bg-white dark:bg-slate-900 border border-dashed border-gray-300 dark:border-slate-700 rounded-lg p-6">
                                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                                    📦 Linked Resource
                                </h4>
                                <p className="text-xs text-gray-500 mb-4">
                                    Attach resource linked to a video timestamp
                                </p>

                                {!hasExistingVideo && !watchedVideoFile && (
                                    <p className="text-sm text-red-500">
                                        Select a video to enable resources
                                    </p>
                                )}

                                {(hasExistingVideo || watchedVideoFile) && (
                                    <>
                                        {/* <FileInputUploadFieldNew
                                            label="Resource File"
                                            name="resourceFile"
                                            control={control}
                                            errors={errors}
                                            preview={resourcePreview}
                                        /> */}


                                        <PdfUploadField
                                            label="Resource File"
                                            name="resourceFile"
                                            control={control}
                                            errors={errors}
                                        />

                                        <input
                                            type="number"
                                            min={0}
                                            disabled={!resourceFile}
                                            value={watchedResourceTimestamp}
                                            onChange={(e) =>
                                                setValue(
                                                    'resourceTimestamp',
                                                    Number(e.target.value)
                                                )
                                            }
                                            placeholder="Timestamp (seconds)"
                                            className="w-full rounded-md border px-3 py-2 text-sm mt-2 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                        />
                                    </>
                                )}

                                {resourceError && (
                                    <p className="text-sm text-red-500 mt-1">
                                        {resourceError}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ===================================================== */}
                        {/* STICKY ACTION BAR */}
                        {/* ===================================================== */}
                        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex justify-end gap-3 z-10">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-6 py-2 bg-gray-300 dark:bg-slate-700 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={
                                    isSaving ||
                                    uploadPhase !== 'idle' ||
                                    (isReplacingVideo && !watchedVideoFile)
                                }
                                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </>
            )}
        </div>
    )
}

export default EditEpisode
